/**
 * Barcode Scanner Tool
 *
 * Mobile-friendly tool for staff to walk around the store and
 * scan product barcodes to attach them to products in the system.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';

interface Product {
  id: number;
  name: string;
  sku: string;
  barcode: string;
  upc: string;
  size_lbs: number | null;
  supplier: string;
  retail_price: number;
}

interface Category {
  id: number | null;
  name: string;
  products: Product[];
  count: number;
}

interface Totals {
  total_products: number;
  missing_barcode: number;
  has_barcode: number;
}

const API_BASE = '/api';

const BarcodeScanner: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [scanMode, setScanMode] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [manualBarcode, setManualBarcode] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'missing' | 'has_barcode' | 'all'>('missing');

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadProducts();
  }, [isAuthenticated, navigate, filter]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/pos/products/barcode-status/?status=${filter}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load products');
      const data = await response.json();
      setCategories(data.categories);
      setTotals(data.totals);
    } catch (err) {
      console.error('Failed to load products:', err);
      setMessage({ type: 'error', text: 'Failed to load products' });
    } finally {
      setLoading(false);
    }
  };

  // zxing fallback reader. See startScanner for why this exists.
  const zxingRef = useRef<BrowserMultiFormatReader | null>(null);

  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      setScanMode(true);

      // BarcodeDetector is a native API that does NOT exist in iOS Safari and
      // is off by default in Firefox. This used to be a bare `if` with no else:
      // on those browsers the camera opened, scanMode went true, the feed
      // played, and nothing ever decoded - no error, no message, no fallback.
      // Staff would report "the scanner is broken" and it would look like a
      // camera fault. Silent failure was the worst of the available options.
      //
      // zxing is a pure-JS decoder that works everywhere, and the codebase
      // already ships it (StoreScan and components/BarcodeScanner both use it).
      if ('BarcodeDetector' in window) {
        startBarcodeDetection();
      } else {
        // Pass the stream we already opened. decodeFromVideoDevice would open a
        // SECOND camera stream alongside this one; decodeFromStream reuses it.
        startZxingDetection(stream);
      }
    } catch (err) {
      console.error('Failed to start camera:', err);
      setMessage({ type: 'error', text: 'Camera access denied. Please enable camera permissions.' });
    }
  };

  const stopScanner = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (zxingRef.current) {
      zxingRef.current.reset();
      zxingRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setScanMode(false);
  }, []);

  const startBarcodeDetection = async () => {
    // @ts-ignore - BarcodeDetector is not in TS types yet
    const barcodeDetector = new window.BarcodeDetector({
      formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf']
    });

    scanIntervalRef.current = window.setInterval(async () => {
      if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
        try {
          const barcodes = await barcodeDetector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const code = barcodes[0].rawValue;
            setScannedBarcode(code);
            setManualBarcode(code);
            // Don't stop scanner automatically - let user verify
          }
        } catch (err) {
          // Ignore detection errors
        }
      }
    }, 100);
  };

  const startZxingDetection = (stream: MediaStream) => {
    // Same outcome as the native path: fill both the scanned and the manual
    // field, and leave the camera running so staff can verify before saving.
    const reader = new BrowserMultiFormatReader();
    zxingRef.current = reader;
    reader.decodeFromStream(stream, videoRef.current!, (result, err) => {
      if (result) {
        const code = result.getText();
        setScannedBarcode(code);
        setManualBarcode(code);
      }
      if (err && !(err instanceof NotFoundException)) {
        console.error('Scan error:', err);
      }
    });
  };

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
    setManualBarcode(product.barcode || '');
    setScannedBarcode('');
    setMessage(null);
  };

  const saveBarcode = async () => {
    if (!selectedProduct || !manualBarcode.trim()) {
      setMessage({ type: 'error', text: 'Please scan or enter a barcode' });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(
        `${API_BASE}/pos/products/${selectedProduct.id}/set-barcode/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ barcode: manualBarcode.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save barcode');
      }

      setMessage({ type: 'success', text: `Barcode saved for ${selectedProduct.name}` });

      // Update local state
      setCategories(cats =>
        cats.map(cat => ({
          ...cat,
          products: cat.products.map(p =>
            p.id === selectedProduct.id
              ? { ...p, barcode: manualBarcode.trim() }
              : p
          )
        }))
      );

      // Clear selection and reload
      setSelectedProduct(null);
      setManualBarcode('');
      setScannedBarcode('');
      loadProducts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const clearBarcode = async () => {
    if (!selectedProduct) return;

    try {
      setSaving(true);
      const response = await fetch(
        `${API_BASE}/pos/products/${selectedProduct.id}/clear-barcode/`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to clear barcode');
      }

      setMessage({ type: 'success', text: `Barcode cleared for ${selectedProduct.name}` });
      setSelectedProduct(null);
      setManualBarcode('');
      loadProducts();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (catName: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catName)) {
        next.delete(catName);
      } else {
        next.add(catName);
      }
      return next;
    });
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>Barcode Scanner</h1>
          <button
            style={styles.backBtn}
            onClick={() => navigate('/store/admin')}
          >
            Back to Admin
          </button>
        </div>

        {/* Stats */}
        {totals && (
          <div style={styles.stats}>
            <div style={styles.statCard}>
              <span style={styles.statValue}>{totals.missing_barcode}</span>
              <span style={styles.statLabel}>Missing</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statValue}>{totals.has_barcode}</span>
              <span style={styles.statLabel}>Has Barcode</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statValue}>{totals.total_products}</span>
              <span style={styles.statLabel}>Total</span>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div style={styles.filterTabs}>
          <button
            style={filter === 'missing' ? styles.filterTabActive : styles.filterTab}
            onClick={() => setFilter('missing')}
          >
            Missing Barcode
          </button>
          <button
            style={filter === 'has_barcode' ? styles.filterTabActive : styles.filterTab}
            onClick={() => setFilter('has_barcode')}
          >
            Has Barcode
          </button>
          <button
            style={filter === 'all' ? styles.filterTabActive : styles.filterTab}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>

        {/* Message */}
        {message && (
          <div style={message.type === 'success' ? styles.successMsg : styles.errorMsg}>
            {message.text}
          </div>
        )}

        <div style={styles.content}>
          {/* Product List */}
          <div style={styles.productList}>
            {loading ? (
              <p style={styles.loading}>Loading products...</p>
            ) : categories.length === 0 ? (
              <p style={styles.empty}>
                {filter === 'missing'
                  ? 'All products have barcodes!'
                  : 'No products found'}
              </p>
            ) : (
              categories.map(cat => (
                <div key={cat.name} style={styles.category}>
                  <button
                    style={styles.categoryHeader}
                    onClick={() => toggleCategory(cat.name)}
                  >
                    <span style={styles.categoryName}>{cat.name}</span>
                    <span style={styles.categoryCount}>{cat.count}</span>
                    <span style={styles.expandIcon}>
                      {expandedCategories.has(cat.name) ? '▼' : '▶'}
                    </span>
                  </button>

                  {expandedCategories.has(cat.name) && (
                    <div style={styles.productItems}>
                      {cat.products.map(product => (
                        <button
                          key={product.id}
                          style={
                            selectedProduct?.id === product.id
                              ? styles.productItemSelected
                              : styles.productItem
                          }
                          onClick={() => selectProduct(product)}
                        >
                          <div style={styles.productInfo}>
                            <span style={styles.productName}>{product.name}</span>
                            <span style={styles.productSku}>
                              SKU: {product.sku}
                              {product.size_lbs && ` | ${product.size_lbs}lb`}
                            </span>
                          </div>
                          {product.barcode && (
                            <span style={styles.barcodeTag}>
                              {product.barcode}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Scanner Panel */}
          <div style={styles.scannerPanel}>
            {selectedProduct ? (
              <>
                <h3 style={styles.selectedTitle}>
                  {selectedProduct.name}
                </h3>
                <p style={styles.selectedSku}>
                  SKU: {selectedProduct.sku}
                </p>

                {/* Camera View */}
                {scanMode ? (
                  <div style={styles.cameraContainer}>
                    <video
                      ref={videoRef}
                      style={styles.video}
                      playsInline
                      muted
                    />
                    <div style={styles.scanOverlay}>
                      <div style={styles.scanLine} />
                    </div>
                    <button
                      style={styles.stopScanBtn}
                      onClick={stopScanner}
                    >
                      Stop Scanner
                    </button>
                  </div>
                ) : (
                  <button
                    style={styles.startScanBtn}
                    onClick={startScanner}
                  >
                    Start Camera Scanner
                  </button>
                )}

                {/* Scanned Result */}
                {scannedBarcode && (
                  <div style={styles.scannedResult}>
                    Detected: <strong>{scannedBarcode}</strong>
                  </div>
                )}

                {/* Manual Entry */}
                <div style={styles.manualEntry}>
                  <label style={styles.inputLabel}>
                    Barcode (scan or type):
                  </label>
                  <input
                    type="text"
                    value={manualBarcode}
                    onChange={e => setManualBarcode(e.target.value)}
                    style={styles.input}
                    placeholder="Enter or scan barcode..."
                  />
                </div>

                {/* Actions */}
                <div style={styles.actions}>
                  <button
                    style={styles.saveBtn}
                    onClick={saveBarcode}
                    disabled={saving || !manualBarcode.trim()}
                  >
                    {saving ? 'Saving...' : 'Save Barcode'}
                  </button>
                  {selectedProduct.barcode && (
                    <button
                      style={styles.clearBtn}
                      onClick={clearBarcode}
                      disabled={saving}
                    >
                      Clear Barcode
                    </button>
                  )}
                  <button
                    style={styles.cancelBtn}
                    onClick={() => {
                      setSelectedProduct(null);
                      setManualBarcode('');
                      setScannedBarcode('');
                      stopScanner();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div style={styles.placeholder}>
                <p style={styles.placeholderText}>
                  Select a product from the list to assign a barcode
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    padding: '1rem',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  backBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#666',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  stats: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 100px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  statValue: {
    display: 'block',
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  statLabel: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.25rem',
  },
  filterTabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  filterTab: {
    padding: '0.5rem 1rem',
    backgroundColor: '#fff',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
  },
  filterTabActive: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a4d1a',
    border: '2px solid #1a4d1a',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#fff',
    fontWeight: '600',
  },
  successMsg: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  errorMsg: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  productList: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    maxHeight: 'calc(100vh - 280px)',
    overflowY: 'auto',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  empty: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  category: {
    marginBottom: '0.5rem',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    gap: '0.5rem',
  },
  categoryName: {
    flex: 1,
    fontWeight: '600',
    color: '#333',
    fontSize: '0.95rem',
  },
  categoryCount: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    padding: '0.125rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.8rem',
  },
  expandIcon: {
    color: '#666',
    fontSize: '0.75rem',
  },
  productItems: {
    marginTop: '0.25rem',
    paddingLeft: '1rem',
  },
  productItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    marginBottom: '0.25rem',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.2s',
  },
  productItemSelected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#e8f5e9',
    border: '2px solid #1a4d1a',
    borderRadius: '6px',
    marginBottom: '0.25rem',
    cursor: 'pointer',
    textAlign: 'left',
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    display: 'block',
    fontWeight: '500',
    color: '#333',
    fontSize: '0.9rem',
  },
  productSku: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#666',
    marginTop: '0.125rem',
  },
  barcodeTag: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
  },
  scannerPanel: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  selectedTitle: {
    margin: '0 0 0.25rem 0',
    fontSize: '1.25rem',
    color: '#333',
  },
  selectedSku: {
    color: '#666',
    margin: '0 0 1rem 0',
    fontSize: '0.9rem',
  },
  cameraContainer: {
    position: 'relative',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '1rem',
  },
  video: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'cover',
  },
  scanOverlay: {
    position: 'absolute',
    top: '50%',
    left: '10%',
    right: '10%',
    height: '2px',
    transform: 'translateY(-50%)',
  },
  scanLine: {
    width: '100%',
    height: '2px',
    backgroundColor: '#ff0000',
    animation: 'scanLine 2s ease-in-out infinite',
  },
  stopScanBtn: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '0.5rem 1rem',
    backgroundColor: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  startScanBtn: {
    width: '100%',
    padding: '1rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '1rem',
  },
  scannedResult: {
    backgroundColor: '#e8f5e9',
    padding: '0.75rem',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '1rem',
    fontSize: '0.95rem',
    color: '#1b5e20',
  },
  manualEntry: {
    marginBottom: '1rem',
  },
  inputLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#333',
    fontSize: '0.9rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    fontFamily: 'monospace',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem 1rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '600',
  },
  clearBtn: {
    padding: '0.75rem 1rem',
    backgroundColor: '#f57c00',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  cancelBtn: {
    padding: '0.75rem 1rem',
    backgroundColor: '#757575',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '2rem',
  },
  placeholderText: {
    color: '#666',
    textAlign: 'center',
    fontSize: '1rem',
  },
};

// Add media query styles for mobile
const mobileStyles = `
  @media (max-width: 768px) {
    .barcode-scanner-content {
      grid-template-columns: 1fr !important;
    }
  }

  @keyframes scanLine {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

// Inject mobile styles
const styleSheet = document.createElement('style');
styleSheet.textContent = mobileStyles;
document.head.appendChild(styleSheet);

export default BarcodeScanner;
